import React, { useRef, useEffect } from 'react';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import { ContactShadows, RoundedBox } from '@react-three/drei';
import * as THREE from 'three';

/**
 * Single real-3D placement preview: a clean room corner (distinct floor + wall
 * shades) with a soft contact shadow. One rounded item glides between a floor
 * placement (sitting, sliding) and a wall placement (hanging, swaying) whenever
 * `placement` changes, via an animated 0→1 blend.
 */

const lerp = (a: number, b: number, t: number) => a + (b - a) * t;

const CameraRig: React.FC = () => {
  const { camera } = useThree();
  useEffect(() => {
    camera.lookAt(0, 0.55, -0.3);
  }, [camera]);
  return null;
};

const Item: React.FC<{ placement: 'floor' | 'wall'; color: string }> = ({ placement, color }) => {
  const ref = useRef<THREE.Group>(null);
  const mix = useRef(placement === 'wall' ? 1 : 0);
  useFrame((state, delta) => {
    const g = ref.current;
    if (!g) return;
    const target = placement === 'wall' ? 1 : 0;
    mix.current += (target - mix.current) * Math.min(1, delta * 3.2);
    const m = mix.current;
    const t = state.clock.elapsedTime;

    const floorX = Math.sin(t * 1.1) * 0.9;
    const wallRz = Math.sin(t * 1.5) * 0.11;

    g.position.x = lerp(floorX, 0, m);
    g.position.y = lerp(0.35, 1.05, m);
    g.position.z = lerp(0.55, -0.78, m);
    g.rotation.y = lerp(0.4 + Math.sin(t * 1.1) * 0.1, 0, m);
    g.rotation.z = lerp(0, wallRz, m);
  });
  return (
    <group ref={ref} position={[0, 0.35, 0.55]}>
      <RoundedBox args={[0.92, 0.68, 0.5]} radius={0.06} smoothness={4}>
        <meshStandardMaterial color={color} roughness={0.5} metalness={0.05} />
      </RoundedBox>
    </group>
  );
};

export const PlacementScene: React.FC<{
  placement: 'floor' | 'wall';
  colors: { item: string; floor: string; wall: string };
}> = ({ placement, colors }) => {
  return (
    <Canvas
      dpr={[1, 2]}
      gl={{ antialias: true, alpha: true }}
      camera={{ position: [2.3, 1.5, 3.2], fov: 30 }}
      style={{ width: '100%', height: '100%' }}
    >
      <CameraRig />
      <ambientLight intensity={0.78} />
      <directionalLight position={[4, 6, 3]} intensity={1.0} />
      <directionalLight position={[-4, 2, -2]} intensity={0.3} />

      {/* floor */}
      <mesh rotation-x={-Math.PI / 2} position={[0, 0, 0.2]}>
        <planeGeometry args={[11, 9]} />
        <meshStandardMaterial color={colors.floor} roughness={1} />
      </mesh>
      {/* back wall */}
      <mesh position={[0, 2, -1.0]}>
        <planeGeometry args={[11, 6]} />
        <meshStandardMaterial color={colors.wall} roughness={1} />
      </mesh>

      {/* soft grounded shadow */}
      <ContactShadows position={[0, 0.002, 0.45]} opacity={0.16} scale={7} blur={3} far={3} resolution={256} color="#000000" />

      <Item placement={placement} color={colors.item} />
    </Canvas>
  );
};

export default PlacementScene;
